import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import KibliLogo from "./KibliLogo";
import InstallPWAButton from "./InstallPWAButton";
import useUnreadMessages from "../lib/useUnreadMessages";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user && user.role === "admin";
  const unread = useUnreadMessages(!!user);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const NavItem = ({ to, icon, label, testid, badge }) => (
    <NavLink
      to={to}
      end={to === "/home"}
      data-testid={testid}
      className={({ isActive }) =>
        `relative flex flex-col md:flex-row items-center justify-center md:justify-start gap-1 md:gap-3 px-3 py-2 md:px-4 md:py-3 rounded-xl text-xs md:text-base font-medium transition-colors ${
          isActive
            ? "text-blue-600 md:bg-blue-50"
            : "text-slate-500 hover:text-blue-600 md:hover:bg-slate-50"
        }`
      }
    >
      <span className="relative">
        <i className={`fa-solid ${icon} text-lg md:text-base`}></i>
        {badge > 0 && (
          <span
            data-testid={`${testid}-badge`}
            className="absolute -top-2 -right-2 md:hidden min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center shadow-md ring-2 ring-white"
          >
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </span>
      <span className="flex-1 md:flex md:items-center md:gap-2">
        {label}
        {badge > 0 && (
          <span
            data-testid={`${testid}-badge-md`}
            className="hidden md:inline-flex min-w-[20px] h-[20px] px-1.5 rounded-full bg-red-500 text-white text-[11px] font-bold items-center justify-center"
          >
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </span>
    </NavLink>
  );

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-slate-50">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:flex-col md:w-64 md:border-r md:border-slate-200 md:bg-white md:px-4 md:py-6 md:sticky md:top-0 md:h-screen">
        <div className="px-2 mb-8">
          <KibliLogo size={40} />
        </div>
        <nav className="flex-1 flex flex-col gap-1">
          <NavItem to="/home" icon="fa-house" label="Inicio" testid="nav-home" />
          <NavItem to="/messages" icon="fa-comment-dots" label="Mensajes" testid="nav-messages" badge={unread} />
          <NavItem to="/profile" icon="fa-user" label="Perfil" testid="nav-profile" />
          {isAdmin && <NavItem to="/admin" icon="fa-shield-halved" label="Admin" testid="nav-admin" />}
        </nav>
        <div className="border-t border-slate-200 pt-4 mt-4 space-y-2">
          <InstallPWAButton variant="sidebar" />
          <div className="px-3 mb-1">
            <div className="text-sm font-semibold text-slate-900 truncate" data-testid="sidebar-user-name">{user?.name}</div>
            <div className="text-xs text-slate-500 truncate">{user?.email}</div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="sidebar-logout-btn"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-xl"
          >
            <i className="fa-solid fa-arrow-right-from-bracket"></i>
            Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Mobile top bar (visible on mobile only) */}
      <header className="md:hidden sticky top-0 z-30 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <KibliLogo size={36} />
        <InstallPWAButton variant="header" />
      </header>

      {/* Main content */}
      <main className="flex-1 pb-24 md:pb-6">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-4 md:py-8">{children}</div>
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-200 px-2 pt-2 pb-[max(8px,env(safe-area-inset-bottom))] flex justify-around">
        <NavItem to="/home" icon="fa-house" label="Inicio" testid="mob-nav-home" />
        <NavItem to="/messages" icon="fa-comment-dots" label="Mensajes" testid="mob-nav-messages" badge={unread} />
        <NavItem to="/profile" icon="fa-user" label="Perfil" testid="mob-nav-profile" />
        {isAdmin && <NavItem to="/admin" icon="fa-shield-halved" label="Admin" testid="mob-nav-admin" />}
      </nav>
    </div>
  );
}
